import React, {FC, ReactNode} from 'react';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {TeamModel} from '../../../models/api/teamModel';
import {WorkspaceFolderLabelName} from '../../../models/api/workspaceModel';
import {TeamAvatar} from '../../common/avatars/teamAvatar';
import {NavigationLineChevron} from '../../common/navigation/navigationLineChevron';
import {NavigationLineIcon} from '../../common/navigation/navigationLineIcon';
import {NavigationLineLabel, NavigationLineLabelProps} from '../../common/navigation/navigationLineLabel';
import {NavigationLineStandardIcon} from '../../common/navigation/navigationLineStandardIcon';
import {WorkspacesEditorRarelyViewedBadge} from './editor/workspacesEditorRarelyViewedBadge';
import {WorkspaceLineDropTarget, WorkspaceLineDropTargetProps} from './line/workspaceLineDropTarget';
import {WorkspaceLineMoreProps} from './line/workspaceLineMore';
import {WorkspaceLineMoreContainer} from './line/workspaceLineMoreContainer';
import {StyledLabelDiv, StyledTextDiv} from './workspaceCustomViewItem';

/*
 * Props.
 */

export interface WorkspaceGenericItemProps
  extends WorkspaceLineDropTargetProps,
    NavigationLineLabelProps,
    Omit<WorkspaceLineMoreProps, 'isGuidedDemoActive'> {
  iconName?: IconName;
  selectedIconName?: IconName;
  onToggleExpandClick?: Handler;
  isExpanded?: boolean;
  label?: WorkspaceFolderLabelName;
  /** Is the workspace in edit mode. */
  isEditingWorkspace?: boolean;
  /** Should the checkbox be selected if we're in editing mode. */
  isChecked?: boolean;
  isEditorCheckboxDisabled?: boolean;
  team?: TeamModel;
  isRecentlyViewed?: boolean;
  children?: ReactNode;
}

/*
 * Component.
 */

export const WorkspaceGenericItem: FC<WorkspaceGenericItemProps> = ({children, ...props}) => (
  <WorkspaceLineDropTarget
    {...props}
    renderTooltip={() => children}
    shouldHidePreviewBadge={props.isEditingWorkspace}
  >
    {maybeRenderChevron(props)}
    {maybeRenderIcon(props)}
    <NavigationLineLabel {...props}>
      <StyledLabelDiv>
        <StyledTextDiv>{children}</StyledTextDiv>
        {props.isRecentlyViewed === false && <WorkspacesEditorRarelyViewedBadge />}
      </StyledLabelDiv>
    </NavigationLineLabel>
    <WorkspaceLineMoreContainer
      renderMenu={props.renderMenu}
      badgeValue={props.badgeValue}
      isUnread={props.isUnread}
      loadingType={props.loadingType}
      shouldShowOfflineIcon={props.shouldShowOfflineIcon}
      isEditingWorkspace={props.isEditingWorkspace}
      isChecked={props.isChecked}
      isEditorCheckboxDisabled={props.isEditorCheckboxDisabled}
      shouldRemoveBadgeLeftSpacing={props.isRecentlyViewed === false}
    />
  </WorkspaceLineDropTarget>
);

/*
 * Helpers.
 */

function maybeRenderChevron(props: WorkspaceGenericItemProps) {
  const {onToggleExpandClick} = props;
  if (!onToggleExpandClick) {
    return null;
  }

  return (
    <NavigationLineChevron
      {...props}
      onToggleExpandClick={onToggleExpandClick}
      isExpanded={props.isExpanded}
    />
  );
}

function maybeRenderIcon(props: WorkspaceGenericItemProps) {
  const {iconName, selectedIconName, isSelected, team} = props;
  if (team) {
    return (
      <NavigationLineIcon>
        <TeamAvatar team={team} size={VisualSizesEnum.SMALL} />
      </NavigationLineIcon>
    );
  }

  if (!iconName) {
    return null;
  }

  const actualIconName = (isSelected && selectedIconName) || iconName;
  return <NavigationLineStandardIcon iconName={actualIconName} />;
}
